// <AdjustToMatchPanel/> — Sprint 10, Task 14
//
// Scores one Portfolio property against one extracted RLP via
// POST /api/portfolio/:id/score, then surfaces the overall, subscores, and
// gaps with remediation guidance. The API call is free — no requireCredits —
// so this panel can refresh on every prop change without cost impact.
//
// 409 from the score endpoint means the RLP requirements haven't been
// extracted yet; we render a soft placeholder instead of an error so the UI
// doesn't look broken on freshly-ingested RLPs.
//
// Doctrine alignment:
//   - Doctrine #1 (AI fabricates nothing): all gap codes come from the
//     scoring engine; recommendations are hard-coded human guidance, not
//     model-generated.
//   - The `malformed_polygon` code ships with a recommendation because
//     Task 11's doctrine-#1 fix emits it when the stored polygon is
//     syntactically invalid.

import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import type { MatchScoreResult } from '@/lib/api-types';

const RECOMMENDATIONS: Record<string, string> = {
  no_property_coords:
    'Add latitude/longitude on this property (geocode the address from the property edit form).',
  outside_polygon:
    'This property is outside the RLP polygon — it cannot match. Consider another property or another RLP.',
  no_aboa:
    'Upload an ALTA survey or add ABOA / rentable_sf manually on the property.',
  aboa_below_min:
    'ABOA is below the RLP minimum. Consider adjacent space if you can lease additional adjacent floors.',
  aboa_above_max:
    'ABOA exceeds the RLP maximum — propose a partial floor lease.',
  no_parking: 'Add parking_count to the property profile.',
  parking_short:
    'Document a parking-sharing agreement with an adjacent lot.',
  no_fsl:
    'Add a security tier compatibility statement (or upload your last security assessment).',
  fsl_short:
    'This property cannot meet the required FSL tier without renovations.',
  energy_low:
    'Upload your most recent energy audit; consider an EnergyStar score upgrade.',
  malformed_polygon:
    'The RLP polygon is malformed in the extraction. Re-run extraction or flag for manual review.',
};

const SUBSCORE_LABELS: Record<keyof MatchScoreResult['subscores'], string> = {
  polygon: 'Polygon',
  size: 'Size (ABOA)',
  parking: 'Parking',
  security: 'Security',
  sustainability: 'Sustainability',
  special: 'Special',
};

export function AdjustToMatchPanel({
  propertyId,
  rlpId,
}: {
  propertyId: string;
  rlpId: string;
}) {
  const [data, setData] = useState<MatchScoreResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notExtracted, setNotExtracted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    setNotExtracted(false);
    try {
      setData(await api.scorePortfolioMatch(propertyId, rlpId));
    } catch (e: unknown) {
      // Gracefully handle the RLP-not-extracted case as an empty state
      // rather than an error banner — the extractor may just not have run.
      if (e instanceof ApiError && e.status === 409) {
        setData(null);
        setNotExtracted(true);
      } else {
        setError(
          e instanceof ApiError
            ? e.detail
            : e instanceof Error
              ? e.message
              : String(e)
        );
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, rlpId]);

  if (loading && !data) {
    return (
      <div className="card flex items-center gap-2 p-4 text-[0.8125rem] text-ink-muted">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Scoring…
      </div>
    );
  }

  if (notExtracted) {
    return (
      <div className="card p-4 text-[0.8125rem] text-ink-muted">
        RLP requirements not extracted yet — run extraction to see a match score.
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-danger-border bg-danger-soft px-4 py-3 text-[0.8125rem] text-danger-bold">
        Failed to score match: {error}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div
      className="card flex flex-col gap-4 p-5"
      data-testid={`adjust-to-match-${propertyId}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-end gap-3">
          <ScoreBadge score={data.overall} />
          <div className="text-[0.6875rem] uppercase tracking-kicker text-ink-muted">
            match score
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void refresh()}
          disabled={loading}
          data-testid={`adjust-to-match-refresh-${propertyId}`}
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Re-score
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-rule pt-3 sm:grid-cols-3">
        {(Object.entries(data.subscores) as Array<
          [keyof MatchScoreResult['subscores'], number]
        >).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-2">
            <span className="text-[0.75rem] text-ink-muted">
              {SUBSCORE_LABELS[k]}
            </span>
            <span className="font-mono text-[0.8125rem] font-semibold tabular-nums text-ink">
              {v}
            </span>
          </div>
        ))}
      </div>

      {data.gaps.length > 0 ? (
        <div className="flex flex-col gap-2 border-t border-rule pt-3">
          <div className="kicker">Gaps & remediation</div>
          <ul className="flex flex-col gap-2">
            {data.gaps.map((g, i) => (
              <li
                key={`${g.code}-${i}`}
                className="rounded-lg border border-warn-border bg-warn-soft px-3 py-2 text-[0.8125rem] text-warn-bold"
                data-testid={`match-gap-${g.code}`}
              >
                <div className="font-semibold">{g.message}</div>
                {RECOMMENDATIONS[g.code] && (
                  <div className="mt-1 text-[0.75rem] font-normal text-ink-muted">
                    {RECOMMENDATIONS[g.code]}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="border-t border-rule pt-3 text-[0.8125rem] text-success-bold">
          No gaps detected — this property passes every requirement check.
        </div>
      )}
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 80
      ? 'text-success-bold ring-success-border bg-success-soft'
      : score >= 60
        ? 'text-warn-bold ring-warn-border bg-warn-soft'
        : 'text-danger-bold ring-danger-border bg-danger-soft';
  return (
    <div
      className={
        'grid h-14 w-14 place-items-center rounded-full ring-2 ring-inset text-[1.0625rem] font-bold tabular-nums ' +
        color
      }
    >
      {score}
    </div>
  );
}
